import { FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Box } from "@mui/system";
import { useCallback, useEffect, useMemo } from "react";
import { useController } from "react-hook-form";
import ErrorMessageHandle from "../Error/ErrorMessageHandle";

const BpIcon = styled("span")(({ theme }) => ({
  borderRadius: "50%",
  width: 20,
  height: 20,
  backgroundColor: theme.palette.customColor.LINE,
  "input:disabled ~ &": {
    boxShadow: "none",
    background: "rgba(206,217,224,.5)",
  },
}));

const BpCheckedIcon = styled(BpIcon)(({ theme }) => ({
  backgroundColor: theme.palette.customColor.PRIMARY,
  "&::before": {
    display: "block",
    width: 20,
    height: 20,
    backgroundImage: `radial-gradient(${theme.palette.customColor.BGLIGHT},${theme.palette.customColor.BGLIGHT} 44%,transparent 49%)`,
    content: '""',
  },
  "input:hover ~ &": {
    backgroundColor: theme.palette.customColor.PRIMARY,
  },
}));

interface RadioOption {
  value: string;
  label: string;
}

interface RadioGroupButtonsProps {
  value: RadioOption[];
  disabled?: boolean;
  activeColor?: "error" | "default" | "primary" | "secondary" | "info" | "success" | "warning";
  handleChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  field: {
    name: string;
    default_value?: string;
    validations?: Array<{ type: string; message?: string }>;
  };
  row?: boolean;
  error: boolean;
  register?: any;
  control: any;
  clearErrors: (fieldName: string) => void;
  data: string;
}

const RadioGroupButtons = ({
  value,
  disabled = false,
  activeColor = "default",
  handleChange,
  field,
  row,
  error,
  control,
  clearErrors,
  data,
}: RadioGroupButtonsProps) => {
  // Memoize validation function to prevent unnecessary re-renders
  const validateRadioGroup = useCallback(
    (selectedValue: string) => {
      const required = field?.validations?.find((x) => x.type === "required");
      if (required && (!selectedValue || selectedValue.length === 0)) {
        return required?.message || "This field is required";
      }
      return true;
    },
    [field?.validations],
  );

  // Use useController for better control over form state changes
  const {
    field: controllerField,
    fieldState: { error: fieldError },
  } = useController({
    name: field?.name,
    control,
    defaultValue: data || field?.default_value || "",
    rules: { validate: validateRadioGroup },
  });

  // Determine current value with proper priority
  const currentValue = useMemo(() => {
    // Priority 1: Controller field value (current form state)
    if (controllerField.value && typeof controllerField.value === "string") {
      return controllerField.value;
    }

    // Priority 2: Data prop (external data)
    if (data && typeof data === "string" && data.trim() !== "") {
      return data;
    }

    // Priority 3: Default value from field
    if (field?.default_value && typeof field.default_value === "string") {
      return field.default_value;
    }

    return "";
  }, [controllerField.value, data, field?.default_value]);

  // Update form value when data prop changes
  useEffect(() => {
    const newValue = data || field?.default_value || "";

    // Only update if the new value is different and valid
    if (newValue !== controllerField.value && newValue !== undefined) {
      // Validate that the new value exists in available options
      const isValidOption = value?.some((option) => option.value === newValue) || newValue === "";

      if (isValidOption) {
        controllerField.onChange(newValue);
      }
    }
  }, [data, field?.default_value, field?.name, controllerField, value]);

  // Handle radio button change with proper error clearing
  const handleRadioChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;

      // Clear field errors when user makes a selection
      if (error && field?.name) {
        clearErrors(field.name);
      }

      // Update form value
      controllerField.onChange(newValue);

      // Call external change handler if provided
      if (handleChange) {
        handleChange(event);
      }
    },
    [error, field?.name, clearErrors, controllerField, handleChange],
  );

  //Memoize rendered options to prevent unnecessary re-renders
  const renderedOptions = useMemo(() => {
    return value?.map((val) => (
      <FormControlLabel
        key={val.value}
        sx={{
          ".Platform-x-FormControlLabel-label": {
            fontSize: "14px",
          },
          marginRight: "30px",
        }}
        value={val.value}
        control={
          <Radio
            disableRipple
            color={activeColor}
            checkedIcon={<BpCheckedIcon />}
            icon={<BpIcon />}
            data-testid={`radio-option-${val.value}`}
          />
        }
        label={val.label}
        disabled={disabled}
      />
    ));
  }, [value, activeColor, disabled]);

  return (
    <Box data-testid={`radio-group-container-${field?.name}`}>
      <RadioGroup
        {...controllerField}
        value={currentValue}
        onChange={handleRadioChange}
        row={row}
        data-testid={`radio-group-${field?.name}`}>
        {renderedOptions}
      </RadioGroup>
      <ErrorMessageHandle error={error || fieldError} />
    </Box>
  );
};

export default RadioGroupButtons;
